import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Mail, Phone, MapPin, Clock, MessageSquare, 
  Twitter, Linkedin, Instagram, Youtube, Github
} from 'lucide-react';
import { useScrollToTop } from '@/hooks/useScrollToTop';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const scrollToTop = useScrollToTop();

  const handleNavigation = (href: string) => {
    // For anchor links (same page), just scroll to top
    if (href.startsWith('#')) {
      scrollToTop();
      return;
    }
    
    // For different pages, navigate and scroll to top
    navigate(href);
    // Use setTimeout to ensure navigation completes before scrolling
    setTimeout(() => {
      scrollToTop();
    }, 100);
  };

  const footerLinks = {
    product: [
      { name: 'Features', href: '/features' },
      { name: 'Pricing', href: '/pricing' },
      { name: 'FAQ', href: '/faq' },
      { name: 'How It Works', href: '/#process' },
      { name: 'Success Stories', href: '/#testimonials' },
    ],
    company: [
      { name: 'About', href: '/about' },
      { name: 'Contact', href: '/contact' },
      { name: 'Careers', href: '/careers' },
    ],
    legal: [
      { name: 'Privacy Policy', href: '/privacy' },
      { name: 'Terms of Service', href: '/terms' },
      { name: 'Cookie Policy', href: '/cookies' },
      { name: 'GDPR', href: '/gdpr' },
    ],
    support: [
      { name: 'Help Center', href: '/help' },
      { name: 'Community', href: '/community' },
      { name: 'Status', href: '/status' },
    ]
  };

  const socialLinks = [
    { name: 'Twitter', href: 'https://twitter.com/30daycashsprint', icon: Twitter },
    { name: 'LinkedIn', href: 'https://linkedin.com/company/30daycashsprint', icon: Linkedin },
    { name: 'Instagram', href: 'https://instagram.com/30daycashsprint', icon: Instagram },
    { name: 'YouTube', href: 'https://youtube.com/@30daycashsprint', icon: Youtube },
    { name: 'GitHub', href: 'https://github.com/30daycashsprint', icon: Github },
  ];

  return (
    <footer className="bg-black text-white">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-6">
              <img src="/StrtupStreak-Icon.png" alt="StartupStreak logo" className="h-10 w-10" />
              <span className="text-2xl font-bold bg-gradient-primary bg-clip-text text-white">
              StartupStreak
              </span>
            </div>
            <p className="text-gray-400 leading-relaxed mb-6 max-w-md">
              Helping entrepreneurs go from idea to revenue in 30 days. 
              AI validation, accountability, and proven playbooks.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 text-gray-400">
                <Mail className="w-4 h-4" />
                <span>hello@startupstreak.com</span>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <Clock className="w-4 h-4" />
                <span>24/7 Support</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-gray-700 hover:text-white transition-colors"
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-6">Product</h3>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.name}>
                  <button 
                    onClick={() => handleNavigation(link.href)}
                    className="text-gray-400 hover:text-white transition-colors text-left w-full"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-6">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <button 
                    onClick={() => handleNavigation(link.href)}
                    className="text-gray-400 hover:text-white transition-colors text-left w-full"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-6">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <button 
                    onClick={() => handleNavigation(link.href)}
                    className="text-gray-400 hover:text-white transition-colors text-left w-full"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-6">Support</h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <button 
                    onClick={() => handleNavigation(link.href)}
                    className="text-gray-400 hover:text-white transition-colors text-left w-full"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-2xl font-semibold text-white mb-4">
              Stay Updated with Sprint Tips
            </h3>
            <p className="text-gray-400 mb-8">
              Get weekly insights on business validation, launch strategies, and entrepreneurial success.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
                Subscribe
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              No spam, unsubscribe anytime. We respect your privacy.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-gray-400 text-sm">
              © {currentYear} StartupStreak. All rights reserved.
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-gray-400">
              <button onClick={() => handleNavigation('/privacy')} className="hover:text-white transition-colors">
                Privacy
              </button>
              <button onClick={() => handleNavigation('/terms')} className="hover:text-white transition-colors">
                Terms
              </button>
              <button onClick={() => handleNavigation('/cookies')} className="hover:text-white transition-colors">
                Cookies
              </button>
              <button onClick={() => handleNavigation('/contact')} className="hover:text-white transition-colors">
                Contact
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
